import { ChatStatus, useStores } from '@littlebird/sdk';
import { useEffect, useState } from 'react';

import { useTabVisible } from '@/hooks/useTabVisible';
import { useWindowFocused } from '@/hooks/useWindowFocused';

import { feedChatsStore, type FeedChatsStore } from './feed-chats.store';
import { currentAssistantReply, isFeedChatSettled } from './use-feed-chats';

export const useMarkFeedChatSeenWhileVisible = (
  threadId: string,
  isOnScreen: boolean,
  store: FeedChatsStore = feedChatsStore
) => {
  'use no memo';

  const { messages, threads } = useStores();
  const isTabVisible = useTabVisible();
  const isWindowFocused = useWindowFocused();
  const windowActive = isTabVisible && isWindowFocused;
  const entry = store.entries.find(
    (candidate) => candidate.threadId === threadId
  );
  const entrySeen = entry?.seen;
  const hasUnseenEntry = entrySeen === false;
  const thread = hasUnseenEntry ? threads.getById(threadId) : undefined;
  const threadStatus = thread?.status;
  const threadMessages = hasUnseenEntry
    ? messages.getMessagesByThreadId(threadId)
    : undefined;
  const reply = threadMessages
    ? currentAssistantReply(threadMessages)
    : undefined;
  const replyStatus = reply?.status;
  const replyFinished =
    threadStatus === ChatStatus.Errored ||
    threadStatus === ChatStatus.Stopped ||
    (reply !== undefined &&
      (replyStatus === ChatStatus.Done ||
        replyStatus === ChatStatus.Stopped ||
        replyStatus === ChatStatus.Errored));
  const isThreadLoaded = hasUnseenEntry && messages.isThreadLoaded(threadId);
  const latestMessageResident =
    !!thread?.lastMessageId && messages.get(thread.lastMessageId) !== undefined;
  const isSettled =
    hasUnseenEntry &&
    entry !== undefined &&
    isFeedChatSettled(entry, thread, threadMessages ?? []);
  const [loadRevision, setLoadRevision] = useState(0);

  useEffect(() => {
    if (entrySeen !== false || !isOnScreen || !isSettled || isThreadLoaded)
      return;

    let cancelled = false;
    void messages
      .loadThreadMessages(threadId)
      .then(() => {
        if (!cancelled) setLoadRevision((revision) => revision + 1);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [entrySeen, isOnScreen, isSettled, isThreadLoaded, messages, threadId]);

  useEffect(() => {
    if (
      entrySeen !== false ||
      !windowActive ||
      !isOnScreen ||
      !isThreadLoaded ||
      !latestMessageResident ||
      !replyFinished ||
      !isSettled
    )
      return;
    store.markSeen(threadId);
  }, [
    entrySeen,
    windowActive,
    isOnScreen,
    isSettled,
    isThreadLoaded,
    latestMessageResident,
    replyFinished,
    replyStatus,
    loadRevision,
    store,
    threadId,
    threadMessages,
    threadStatus,
  ]);
};
